import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    FormHelperText
} from '@material-ui/core';
import useForm from 'react-hook-form';

import client from '../../../../utils/client';
import Alert from '../../../../utils/alert';
import useModal from '../../../hooks/useModal';

const PASSWORD_MIN_LENGTH = 5;
const PASSWORD_MAX_LENGTH = 50;

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(3),
    },
    button: {
        fontSize: '1.6rem',
        padding: theme.spacing(1, 3),
    },
    dialog: {
        '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 650,
            padding: theme.spacing(2),
        },
        '& .MuiDialogContent-root': {
            overflowY: 'visible',
        },
    },
    dialogTitle: {
        '& .MuiTypography-root': {
            fontSize: '1.8rem',
            fontWeight: 500,
            textAlign: 'center',
        },
    },
    dialogContent: {
        padding: theme.spacing(2, 3),
        '&:first-child': {
            paddingTop: theme.spacing(2),
        },
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    textField: {
        marginBottom: theme.spacing(3),
        '& .MuiInputBase-input': {
            fontSize: '1.6rem',
        },
        '& .MuiInputLabel-root': {
            fontSize: '1.2rem',
        },
        '& .MuiFormHelperText-root': {
            fontSize: '1rem',
            marginTop: theme.spacing(0.5),
        },
    },
    validationText: {
        fontSize: '1rem',
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    validationSuccess: {
        color: theme.palette.success.main,
    },
    validationError: {
        color: theme.palette.error.main,
    },
    validationNeutral: {
        color: theme.palette.text.secondary,
    },
    actions: {
        padding: theme.spacing(2, 3),
        '& > *': {
            margin: theme.spacing(0, 1),
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 2),
        },
    },
    submitButton: {
        fontSize: '1.6rem',
        padding: theme.spacing(1, 3),
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.2rem',
            padding: theme.spacing(0.5, 2),
            minWidth: '80px',
        },
    },
    cancelButton: {
        fontSize: '1.6rem',
        padding: theme.spacing(1, 3),
        backgroundColor: theme.palette.error.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.2rem',
            padding: theme.spacing(0.5, 2),
            minWidth: '80px',
        },
    },
}));

const PasswordEditor = () => {
    const classes = useStyles();
    const [openModal, closeModal, modalIsOpen] = useModal();
    const methods = useForm();
    const { handleSubmit, register } = methods;

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [validations, setValidations] = useState({
        length: { valid: false, message: '' },
        number: { valid: false, message: '' },
        uppercase: { valid: false, message: '' },
        lowercase: { valid: false, message: '' },
        match: { valid: false, message: '' }
    });

    const validatePassword = (newPassword, newConfirm) => {
        const newValidations = {
            length: {
                valid: newPassword.length >= PASSWORD_MIN_LENGTH && newPassword.length <= PASSWORD_MAX_LENGTH,
                message: newPassword.length === 0
                    ? 'パスワードを入力してください'
                    : newPassword.length < PASSWORD_MIN_LENGTH
                        ? `${PASSWORD_MIN_LENGTH}文字以上入力してください`
                        : newPassword.length > PASSWORD_MAX_LENGTH
                            ? `${PASSWORD_MAX_LENGTH}文字以下で入力してください`
                            : '✓ 長さは適切です'
            },
            match: {
                valid: newPassword === newConfirm && newPassword !== '',
                message: newPassword.length === 0
                    ? '確認用パスワードを入力してください'
                    : newConfirm.length === 0
                        ? 'もう一度パスワードを入力してください'
                        : newPassword === newConfirm
                            ? '✓ パスワードが一致しています'
                            : 'パスワードが一致しません'
            }
        };
        setValidations(newValidations);
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        validatePassword(newPassword, confirmPassword);
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirm = e.target.value;
        setConfirmPassword(newConfirm);
        validatePassword(password, newConfirm);
    };

    const ValidationMessage = ({ validation }) => (
        <FormHelperText
            className={`${classes.validationText} ${validation.valid
                ? classes.validationSuccess
                : password.length === 0
                    ? classes.validationNeutral
                    : classes.validationError
                }`}
        >
            {validation.message}
        </FormHelperText>
    );

    const onSubmit = data => {
        if (Object.values(validations).every(v => v.valid)) {
            client.put('auth/password', { password: password })
                .then(res => {
                    Alert.success("パスワードを変更しました");
                })
                .catch(errors => {
                    console.log(errors);
                    Alert.error("パスワードの変更に失敗しました")
                })
                .finally(() => {
                    closeModal();
                });
        }
    };

    return (
        <Box className={classes.root}>
            <Button
                variant="contained"
                color="primary"
                onClick={openModal}
                className={classes.button}
            >
                パスワード変更
            </Button>

            <Dialog
                open={modalIsOpen}
                onClose={closeModal}
                className={classes.dialog}
                aria-labelledby="password-dialog-title"
            >
                <DialogTitle id="password-dialog-title" className={classes.dialogTitle}>
                    パスワード変更
                </DialogTitle>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent className={classes.dialogContent}>
                        <TextField
                            fullWidth
                            label="新しいパスワード"
                            type="password"
                            placeholder="パスワードを入力してください"
                            className={classes.textField}
                            value={password}
                            onChange={handlePasswordChange}
                            {...register("password", {
                                required: true,
                                minLength: PASSWORD_MIN_LENGTH,
                                maxLength: PASSWORD_MAX_LENGTH
                            })}
                        />

                        <ValidationMessage validation={validations.length} />

                        <TextField
                            fullWidth
                            label="パスワード確認"
                            type="password"
                            placeholder="パスワードをもう一度入力してください"
                            className={classes.textField}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            {...register("password2", {
                                required: true,
                                validate: value => value === password
                            })}
                        />
                        <ValidationMessage validation={validations.match} />
                    </DialogContent>

                    <DialogActions className={classes.actions}>
                        <Button
                            type="submit"
                            className={classes.submitButton}
                            disabled={!Object.values(validations).every(v => v.valid)}
                        >
                            変更
                        </Button>
                        <Button
                            onClick={closeModal}
                            className={classes.cancelButton}
                        >
                            キャンセル
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default PasswordEditor;
